<script lang="ts">
/**
 * Which part of an extraction this row shows. A closed set: the component never
 * reads an arbitrary key off the record.
 */
export type GaiaExtractionFieldKey =
  'ai_value' | 'ai_reason' | 'ai_page' | 'ai_highlighted_text' | 'evaluation' | 'evaluation_reason';

/** The six editable parts, as the overwrite endpoint wants them. */
export interface GaiaExtractionEditValues {
  value: GaiaJsonValue;
  reason: string;
  page: string;
  highlighted_text: string[];
  evaluation: string;
  evaluation_reason: string;
}
</script>

<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import type { GaiaJsonValue } from '@/api';
import type { ExtractedField, FieldType } from '@/types/evaluation';
import { getFieldCategory } from '@/utils/fieldType';
import GaiaAddButton from '../../base/GaiaAddButton.vue';
import GaiaDeleteButton from '../../base/GaiaDeleteButton.vue';
import GaiaInput from '../../base/inputs/GaiaInput.vue';
import GaiaSelect from '../../base/selects/GaiaSelect.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaTypedValue from './GaiaTypedValue.vue';
import GaiaTypedValueEditor from './GaiaTypedValueEditor.vue';
import { gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  keyName: {
    type: String as PropType<GaiaExtractionFieldKey | (string & {})>,
    required: true,
  },
  /** Only read by the fallback branch, for a key outside the closed set. */
  value: {
    // Required rather than defaulted: `GaiaJsonValue` is `unknown`, and any
    // literal default collapses the inferred prop type to that literal — and a
    // cast that would preserve it is stripped as redundant by eslint --fix.
    // Pass an explicit `null` for "no value"; there is no row without one.
    //
    // `type: null` means "no runtime check", which is the honest declaration
    // here: this is a JSON value, so `null` is one of its legal shapes. The
    // constructor list this replaces rejected exactly the `null` the line above
    // asks callers to pass, and warned on every field with no evaluation reason.
    type: null as unknown as PropType<GaiaJsonValue>,
    required: true,
  },
  fieldMeta: {
    type: Object as PropType<ExtractedField>,
    required: true,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:editValues': [values: GaiaExtractionEditValues] }>();

const editValues = ref<GaiaExtractionEditValues>({
  value: '',
  reason: '',
  page: '',
  highlighted_text: [],
  evaluation: '',
  evaluation_reason: '',
});

/**
 * The risk vocabulary a human can pick when overriding the AI's evaluation.
 * Not a backend enum — `evaluation` is untyped JSON and these four strings are
 * what the product has always offered.
 */
const evaluationOptions = [
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Human Review', value: 'review' },
  { label: 'Low', value: 'low' },
];

function mapEvaluationValue(displayValue: string): string {
  const mapping: Record<string, string> = {
    High: 'high',
    Medium: 'medium',
    'Human Review': 'review',
    Low: 'low',
  };
  return mapping[displayValue] || displayValue;
}

const overwrite = computed(() => ({
  value: props.fieldMeta.has_value_overwrite,
  reason: props.fieldMeta.has_reason_overwrite,
  page: props.fieldMeta.has_page_overwrite,
  highlightedText: props.fieldMeta.has_highlighted_text_overwrite,
  evaluationAnswer: props.fieldMeta.has_evaluation_overwrite,
  evaluationReason: props.fieldMeta.has_evaluation_reason_overwrite,
}));

/** The highlighted quotes stay a list — each one is a separate span of the document. */
const highlightedText = computed<string[]>(() =>
  overwrite.value.highlightedText
    ? (props.fieldMeta.overwritten_highlighted_text ?? [])
    : (props.fieldMeta.ai_highlighted_text ?? [])
);

/**
 * Every view branch is the same component over a different (value, type) pair,
 * so the branching is here rather than six times in the template.
 */
const shown = computed<{ value: GaiaJsonValue; fieldType: FieldType }>(() => {
  const meta = props.fieldMeta;
  switch (props.keyName) {
    case 'ai_value': {
      return {
        value: overwrite.value.value ? meta.overwritten_value : meta.ai_value,
        fieldType: meta.field_type ?? 'str',
      };
    }
    case 'ai_reason': {
      return {
        value: (overwrite.value.reason ? meta.overwritten_reason : meta.ai_reason) ?? null,
        fieldType: 'str',
      };
    }
    case 'ai_page': {
      return {
        value: (overwrite.value.page ? meta.overwritten_page : meta.ai_page) ?? null,
        fieldType: 'int',
      };
    }
    case 'evaluation': {
      return {
        value:
          (overwrite.value.evaluationAnswer
            ? meta.overwritten_evaluation
            : meta.ai_evaluation?.answer) ?? null,
        fieldType: 'str',
      };
    }
    case 'evaluation_reason': {
      return {
        value:
          (overwrite.value.evaluationReason
            ? meta.overwritten_evaluation_reason
            : meta.ai_evaluation?.reason) ?? null,
        fieldType: 'str',
      };
    }
    default: {
      return { value: props.value, fieldType: 'str' };
    }
  }
});

/** What the AI said, shown under the editor so an override has something to compare against. */
const originalHint = computed(() => {
  const meta = props.fieldMeta;
  switch (props.keyName) {
    case 'ai_reason': {
      return { label: 'Extracted', value: meta.ai_reason ?? 'N/A' };
    }
    case 'ai_page': {
      return { label: 'Extracted', value: String(meta.ai_page ?? 'N/A') };
    }
    case 'ai_highlighted_text': {
      return { label: 'Extracted', value: meta.ai_highlighted_text?.join('; ') ?? 'N/A' };
    }
    case 'evaluation': {
      return { label: 'AI result', value: meta.ai_evaluation?.answer ?? 'N/A' };
    }
    case 'evaluation_reason': {
      return { label: 'AI reasoning', value: meta.ai_evaluation?.reason ?? 'N/A' };
    }
    default: {
      return null;
    }
  }
});

watch(
  () => props.editMode,
  newEditMode => {
    if (newEditMode) {
      initializeEditValues();
    } else {
      resetEditValues();
    }
  }
);

watch(
  editValues,
  newValues => {
    if (props.editMode) {
      emit('update:editValues', newValues);
    }
  },
  { deep: true }
);

function initializeEditValues(): void {
  const currentValue = props.fieldMeta.has_value_overwrite
    ? props.fieldMeta.overwritten_value
    : props.fieldMeta.ai_value;

  // Seed the editor with a value of the shape its category expects, so a null
  // or a type-mismatched leftover doesn't land in a control that can't hold it.
  let valueToEdit: GaiaJsonValue;
  switch (getFieldCategory(props.fieldMeta.field_type)) {
    case 'number': {
      valueToEdit = typeof currentValue === 'number' ? currentValue : null;
      break;
    }
    case 'list':
    case 'enum-multiple': {
      valueToEdit = Array.isArray(currentValue) ? currentValue : [];
      break;
    }
    case 'currency': {
      valueToEdit =
        typeof currentValue === 'object' && currentValue !== null
          ? currentValue
          : { amount: 0, iso_4217_alpha_3: 'USD' };
      break;
    }
    case 'boolean': {
      valueToEdit = typeof currentValue === 'boolean' ? currentValue : false;
      break;
    }
    case 'entity': {
      valueToEdit =
        typeof currentValue === 'object' && currentValue !== null
          ? currentValue
          : { name: '', address: '' };
      break;
    }
    case 'enum-single': {
      valueToEdit = typeof currentValue === 'string' ? currentValue : '';
      break;
    }
    case 'datetime': {
      valueToEdit = currentValue ?? null;
      break;
    }
    default: {
      valueToEdit = currentValue;
    }
  }

  editValues.value = {
    value: valueToEdit,
    reason: props.fieldMeta.has_reason_overwrite
      ? (props.fieldMeta.overwritten_reason ?? '')
      : (props.fieldMeta.ai_reason ?? ''),
    page: props.fieldMeta.has_page_overwrite
      ? String(props.fieldMeta.overwritten_page ?? '')
      : String(props.fieldMeta.ai_page ?? ''),
    highlighted_text: props.fieldMeta.has_highlighted_text_overwrite
      ? [...(props.fieldMeta.overwritten_highlighted_text ?? [])]
      : [...(props.fieldMeta.ai_highlighted_text ?? [])],
    evaluation: props.fieldMeta.has_evaluation_overwrite
      ? mapEvaluationValue(String(props.fieldMeta.overwritten_evaluation ?? ''))
      : mapEvaluationValue(String(props.fieldMeta.ai_evaluation?.answer ?? '')),
    evaluation_reason: props.fieldMeta.has_evaluation_reason_overwrite
      ? (props.fieldMeta.overwritten_evaluation_reason ?? '')
      : (props.fieldMeta.ai_evaluation?.reason ?? ''),
  };
}

function resetEditValues(): void {
  editValues.value = {
    value: null,
    reason: '',
    page: '',
    highlighted_text: [],
    evaluation: '',
    evaluation_reason: '',
  };
}

const quoteBorder = gaiaNeutral.borderSubtle;
</script>

<template>
  <!-- One part of an extraction — the value, its reason, its page, the quotes it
       came from, or the evaluation — in read or edit mode. The row owns the
       per-part editor; the *card* around it owns the label and the Save/Cancel.
       Read mode is one GaiaTypedValue for every part, because a part is only a
       value and the type to read it with. -->
  <n-flex vertical :size="6" :wrap="false" :style="{ width: '100%' }">
    <template v-if="props.editMode">
      <gaia-typed-value-editor
        v-if="props.keyName === 'ai_value'"
        :field-type="props.fieldMeta.field_type ?? 'str'"
        :value="editValues.value"
        :enum-options="props.fieldMeta.enum_options"
        @update:value="(newValue: GaiaJsonValue) => (editValues.value = newValue)"
      />
      <gaia-input
        v-else-if="props.keyName === 'ai_reason'"
        v-model:value="editValues.reason"
        type="textarea"
        :autosize="{ minRows: 2, maxRows: 6 }"
        placeholder="Enter new reason"
      />
      <gaia-input
        v-else-if="props.keyName === 'ai_page'"
        v-model:value="editValues.page"
        placeholder="Enter page number"
      />
      <n-flex v-else-if="props.keyName === 'ai_highlighted_text'" vertical :size="8" :wrap="false">
        <n-flex
          v-for="(_item, index) in editValues.highlighted_text"
          :key="index"
          align="center"
          :size="8"
          :wrap="false"
        >
          <gaia-input
            v-model:value="editValues.highlighted_text[index]"
            placeholder="Enter highlighted text item"
            :style="{ flex: 1, minWidth: 0 }"
          />
          <gaia-delete-button
            intent="remove"
            variant="label"
            :style="{ flex: 'none' }"
            @click="editValues.highlighted_text.splice(index, 1)"
          />
        </n-flex>
        <gaia-add-button
          subject="item"
          :style="{ alignSelf: 'flex-start' }"
          @click="editValues.highlighted_text.push('')"
        />
      </n-flex>
      <gaia-select
        v-else-if="props.keyName === 'evaluation'"
        v-model:value="editValues.evaluation"
        :options="evaluationOptions"
        placeholder="Select evaluation result"
      />
      <gaia-input
        v-else-if="props.keyName === 'evaluation_reason'"
        v-model:value="editValues.evaluation_reason"
        type="textarea"
        :autosize="{ minRows: 2, maxRows: 6 }"
        placeholder="Enter evaluation reasoning"
      />

      <gaia-text v-if="originalHint" size="meta" tone="subtle">
        {{ originalHint.label }}: {{ originalHint.value }}
      </gaia-text>
    </template>

    <!-- Read mode -->
    <n-flex v-else-if="props.keyName === 'ai_highlighted_text'" vertical :size="6" :wrap="false">
      <gaia-text
        v-for="(quote, index) in highlightedText"
        :key="index"
        tag="div"
        size="row"
        tone="text"
        class="quote"
      >
        {{ quote }}
      </gaia-text>
      <gaia-typed-value v-if="highlightedText.length === 0" :value="null" />
    </n-flex>
    <gaia-typed-value v-else :value="shown.value" :field-type="shown.fieldType" />
  </n-flex>
</template>

<style scoped>
/* Each quote is a span lifted out of the document, so it gets its own rule
   rather than reading as one run of prose. */
.quote {
  padding-left: 10px;
  border-left: 2px solid v-bind(quoteBorder);
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
